import React, { useEffect, useState } from "react";
import { api, money, SPECIALTIES, specialtyColor } from "../lib/api";
import { useLang } from "../context/LanguageContext";
import { toast } from "sonner";
import { Search, Package, ChevronRight, TrendingUp, X, Plus, Trash2, Upload, Undo2, Loader2, ListChecks } from "lucide-react";
import { SupplierPicker } from "../components/SupplierPicker";

const inputCls = "rounded-lg border border-slate-300 px-3 py-2 text-sm focus:ring-2 focus:ring-slate-900 focus:border-slate-900 outline-none transition";

export default function Materials() {
  const { t, lang } = useLang();
  const locale = lang === "en" ? "en-US" : "pt-BR";
  const [tab, setTab] = useState("history"); // history | price
  const [rows, setRows] = useState([]);
  const [groups, setGroups] = useState([]);
  const [q, setQ] = useState("");
  const [supplier, setSupplier] = useState("");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [trade, setTrade] = useState("");
  const [expanded, setExpanded] = useState(null);
  const emptyAdd = { material_name: "", quantity: 1, unit_price: 0, supplier: "", purchased_date: new Date().toISOString().slice(0, 10), trade: "general_repair", note: "" };
  const [addOpen, setAddOpen] = useState(false);
  const [addForm, setAddForm] = useState(emptyAdd);
  const [importing, setImporting] = useState(false);
  const [lastBatch, setLastBatch] = useState(null);
  const MAT_COLS = [
    { k: "sku", label: "SKU" },
    { k: "department", tk: "colDepartment" },
    { k: "class_name", tk: "colClass" },
    { k: "subclass", tk: "colSubclass" },
    { k: "project", tk: "colProject" },
    { k: "purchaser", tk: "colPurchaser" },
    { k: "order_number", tk: "colOrderNum" },
    { k: "invoice_number", tk: "colInvoiceNum" },
    { k: "transaction_id", tk: "colTransaction" },
    { k: "store_number", tk: "colStore" },
    { k: "discount", tk: "colDiscount", money: true },
    { k: "note", tk: "notes" },
  ];
  const [cols, setCols] = useState(() => {
    try { return JSON.parse(localStorage.getItem("jmo_mat_cols")) || ["sku", "department"]; }
    catch { return ["sku", "department"]; }
  });
  const [colsOpen, setColsOpen] = useState(false);
  const toggleCol = (c) => {
    const next = cols.includes(c) ? cols.filter((x) => x !== c) : [...cols, c];
    setCols(next);
    localStorage.setItem("jmo_mat_cols", JSON.stringify(next));
  };
  const activeCols = MAT_COLS.filter((c) => cols.includes(c.k));

  const [importPct, setImportPct] = useState(0);
  const importExcel = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setImporting(true); setImportPct(0);
    try {
      const buf = await file.arrayBuffer();
      const hashBuf = await crypto.subtle.digest("SHA-256", buf);
      const hash = [...new Uint8Array(hashBuf)].map((b) => b.toString(16).padStart(2, "0")).join("");
      const XLSX = await import("xlsx");
      const wb = XLSX.read(buf, { type: "array", cellDates: true });
      const ws = wb.Sheets[wb.SheetNames[0]];
      const aoa = XLSX.utils.sheet_to_json(ws, { header: 1, raw: true, defval: null });
      if (!aoa.length) throw new Error("empty");
      const header = aoa[0].map((h) => String(h ?? ""));
      const dataRows = aoa.slice(1);
      const { batch_id } = await api.post("/materials/import-begin", { filename: file.name, file_hash: hash });
      const CHUNK = 1000;
      for (let i = 0; i < dataRows.length; i += CHUNK) {
        const rows = dataRows.slice(i, i + CHUNK).map((r) => r.map((v) => (v instanceof Date ? v.toISOString() : v ?? null)));
        await api.post("/materials/import-rows", { batch_id, header, rows });
        setImportPct(Math.min(99, Math.round(((i + CHUNK) / dataRows.length) * 100)));
      }
      const res = await api.post("/materials/import-commit", { batch_id });
      setLastBatch(res);
      toast.success(`${t("imported")}: ${res.imported}`);
      loadHistory(); loadPrice();
    } catch (err) {
      toast.error(err?.response?.data?.detail || t("error"));
    } finally { setImporting(false); setImportPct(0); e.target.value = ""; }
  };

  const undoImport = async () => {
    if (!lastBatch || !window.confirm(t("confirmDelete"))) return;
    await api.del(`/materials/import/${lastBatch.batch_id}`);
    toast.success(t("deleted"));
    setLastBatch(null);
    loadHistory(); loadPrice();
  };

  const loadHistory = (tr = trade) => {
    const p = new URLSearchParams();
    if (q) p.append("q", q);
    if (supplier) p.append("supplier", supplier);
    if (dateFrom) p.append("date_from", dateFrom);
    if (dateTo) p.append("date_to", dateTo);
    if (tr) p.append("trade", tr);
    api.get(`/materials?${p.toString()}`).then(setRows).catch(() => {});
  };
  const pickTrade = (tr) => { const next = trade === tr ? "" : tr; setTrade(next); loadHistory(next); };
  const changeTrade = async (row, tr) => {
    try { await api.put(`/materials/${row.id}/trade`, { trade: tr }); toast.success(t("saved")); loadHistory(); }
    catch { toast.error(t("error")); }
  };
  const loadPrice = () => api.get("/materials/price-history").then((r) => setGroups(r.materials || [])).catch(() => {});

  const saveStock = async () => {
    if (!addForm.material_name.trim()) return toast.error(t("materialsDb"));
    try {
      await api.post("/materials", {
        ...addForm,
        quantity: parseFloat(addForm.quantity) || 1,
        unit_price: parseFloat(addForm.unit_price) || 0,
        purchased_date: new Date(addForm.purchased_date).toISOString(),
      });
      toast.success(t("saved"));
      setAddOpen(false); setAddForm(emptyAdd);
      loadHistory(); loadPrice();
    } catch { toast.error(t("error")); }
  };
  const removeStock = async (id) => {
    if (!window.confirm(t("confirmDelete"))) return;
    try { await api.del(`/materials/${id}`); toast.success(t("deleted")); loadHistory(); loadPrice(); }
    catch { toast.error(t("error")); }
  };

  useEffect(() => { loadHistory(); loadPrice(); }, []); // eslint-disable-line
  const clear = () => { setQ(""); setSupplier(""); setDateFrom(""); setDateTo(""); setTimeout(loadHistory, 0); };
  const fmtDate = (iso) => iso ? new Date(iso).toLocaleDateString(locale, { month: "short", day: "numeric", year: "numeric" }) : "—";

  return (
    <div className="space-y-5">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h1 className="text-2xl md:text-3xl font-bold tracking-tight text-slate-900">{t("materialsDb")}</h1>
        <div className="flex items-center gap-2">
          {lastBatch && (
            <button data-testid="undo-import-btn" onClick={undoImport}
              className="inline-flex items-center gap-2 rounded-lg border border-red-200 text-red-600 text-sm font-medium px-3 py-2.5 hover:bg-red-50 transition-colors">
              <Undo2 className="w-4 h-4" /> {t("undoImport")} ({lastBatch.imported})
            </button>
          )}
          <label data-testid="import-excel-btn" className="inline-flex items-center gap-2 rounded-lg border border-slate-300 text-slate-700 text-sm font-medium px-4 py-2.5 hover:bg-slate-50 transition-colors cursor-pointer">
            {importing ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />} {importing ? `${importPct}%` : t("importExcel")}
            <input type="file" accept=".xlsx" className="hidden" onChange={importExcel} disabled={importing} />
          </label>
          <button data-testid="add-stock-purchase-btn" onClick={() => { setAddForm(emptyAdd); setAddOpen(true); }}
            className="inline-flex items-center gap-2 rounded-lg bg-slate-900 text-white text-sm font-medium px-4 py-2.5 hover:bg-slate-800 transition-colors">
            <Plus className="w-4 h-4" /> {t("addStockPurchase")}
          </button>
        </div>
      </div>

      <div className="flex gap-2 border-b border-slate-200">
        <button data-testid="tab-history" onClick={() => setTab("history")} className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px ${tab === "history" ? "border-slate-900 text-slate-900" : "border-transparent text-slate-400 hover:text-slate-600"}`}>{t("purchaseHistory")}</button>
        <button data-testid="tab-price" onClick={() => setTab("price")} className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px ${tab === "price" ? "border-slate-900 text-slate-900" : "border-transparent text-slate-400 hover:text-slate-600"}`}>{t("priceHistory")}</button>
      </div>

      {tab === "history" && (
        <>
          <div className="flex flex-wrap items-center gap-2">
            <button data-testid="trade-filter-all" onClick={() => pickTrade("")} className={`px-3 py-1.5 rounded-full text-xs font-semibold transition-colors ${!trade ? "bg-slate-900 text-white" : "bg-white border border-slate-200 text-slate-600 hover:bg-slate-50"}`}>{t("allTrades")}</button>
            {SPECIALTIES.map((s) => (
              <button key={s} data-testid={`trade-filter-${s}`} onClick={() => pickTrade(s)}
                className={`px-3 py-1.5 rounded-full text-xs font-semibold transition-colors ${trade === s ? "bg-slate-900 text-white" : `${specialtyColor(s)} hover:opacity-80`}`}>
                {t(`sp_${s}`)}
              </button>
            ))}
          </div>
          <div className="flex flex-wrap items-end gap-2">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <input data-testid="mat-search" value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={(e) => e.key === "Enter" && loadHistory()} placeholder={t("searchMaterial")} className={`${inputCls} pl-9 w-56`} />
            </div>
            <input data-testid="mat-supplier" value={supplier} onChange={(e) => setSupplier(e.target.value)} placeholder={t("filterSupplier")} className={`${inputCls} w-40`} />
            <div><label className="block text-[10px] uppercase font-bold text-slate-500 mb-0.5">{t("dateFrom")}</label><input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} className={inputCls} /></div>
            <div><label className="block text-[10px] uppercase font-bold text-slate-500 mb-0.5">{t("dateTo")}</label><input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} className={inputCls} /></div>
            <button data-testid="mat-apply" onClick={loadHistory} className="rounded-lg bg-slate-900 text-white text-sm font-medium px-4 py-2 hover:bg-slate-800">{t("search")}</button>
            <button onClick={clear} className="rounded-lg border border-slate-300 text-sm px-3 py-2 hover:bg-slate-50 inline-flex items-center gap-1"><X className="w-3.5 h-3.5" />{t("clearFilters")}</button>
            <div className="relative">
              <button data-testid="mat-columns-btn" onClick={() => setColsOpen(!colsOpen)}
                className="inline-flex items-center gap-2 rounded-lg border border-slate-300 bg-white px-3.5 py-2 text-sm font-medium text-slate-600 hover:bg-slate-50">
                <ListChecks className="w-4 h-4" />{t("columns")}
                {cols.length > 0 && <span className="text-[10px] font-bold bg-slate-900 text-white rounded-full px-1.5 py-0.5">{cols.length}</span>}
              </button>
              {colsOpen && (
                <div data-testid="mat-columns-panel" className="absolute right-0 top-full mt-1 z-40 bg-white border border-slate-200 rounded-xl shadow-lg p-2 w-56">
                  {MAT_COLS.map((c) => (
                    <label key={c.k} data-testid={`mat-col-toggle-${c.k}`} className="flex items-center gap-2.5 px-2.5 py-2 rounded-lg hover:bg-slate-50 cursor-pointer text-sm text-slate-700">
                      <input type="checkbox" checked={cols.includes(c.k)} onChange={() => toggleCol(c.k)} className="rounded border-slate-300" />
                      {c.label || t(c.tk)}
                    </label>
                  ))}
                </div>
              )}
            </div>
          </div>

          <div className="bg-white border border-slate-200 rounded-xl shadow-sm overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-slate-50 border-b border-slate-200 text-xs font-bold uppercase tracking-wider text-slate-500">
                    <th className="text-left px-4 py-3">{t("purchasedOn")}</th>
                    <th className="text-left px-4 py-3">{t("materialsDb")}</th>
                    {activeCols.map((c) => <th key={c.k} className={`px-4 py-3 ${c.money ? "text-right" : "text-left"}`}>{c.label || t(c.tk)}</th>)}
                    <th className="text-left px-4 py-3">{t("forJob")}</th>
                    <th className="text-left px-4 py-3">{t("supplier")}</th>
                    <th className="text-left px-4 py-3">{t("tradeLabel")}</th>
                    <th className="text-right px-4 py-3">Qty</th>
                    <th className="text-right px-4 py-3">{t("actualPrice")}</th>
                    <th className="text-right px-4 py-3">{t("total")}</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((r) => (
                    <tr key={r.id} data-testid={`mat-row-${r.id}`} className="border-b border-slate-100 hover:bg-slate-50">
                      <td className="px-4 py-3 text-slate-500 whitespace-nowrap">{fmtDate(r.purchased_date)}</td>
                      <td className="px-4 py-3">
                        <div className="font-medium text-slate-900">{r.material_name}</div>
                      </td>
                      {activeCols.map((c) => (
                        <td key={c.k} className={`px-4 py-3 text-slate-600 ${c.money ? "text-right tabular-nums" : ""}`}>
                          {c.money ? (r[c.k] ? money(r[c.k]) : "—") : (r[c.k] || "—")}
                        </td>
                      ))}
                      <td className="px-4 py-3 text-slate-500">
                        {r.source === "stock" || r.source === "import"
                          ? <span className="inline-flex items-center gap-1 text-[11px] font-semibold px-2 py-0.5 rounded-full bg-amber-100 text-amber-700"><Package className="w-3 h-3" />{r.source === "import" ? t("importBadge") : t("stockBadge")}</span>
                          : (r.unit_name || "—")}
                      </td>
                      <td className="px-4 py-3 text-slate-600">{r.supplier || "—"}</td>
                      <td className="px-4 py-3">
                        <select data-testid={`mat-trade-${r.id}`} value={r.trade || "general_repair"} onChange={(e) => changeTrade(r, e.target.value)}
                          className={`text-[11px] font-semibold rounded-full px-2 py-1 border-0 cursor-pointer ${specialtyColor(r.trade || "general_repair")}`}>
                          {SPECIALTIES.map((s) => <option key={s} value={s}>{t(`sp_${s}`)}</option>)}
                        </select>
                      </td>
                      <td className="px-4 py-3 text-right tabular-nums">{r.quantity}</td>
                      <td className="px-4 py-3 text-right tabular-nums">{money(r.unit_price)}</td>
                      <td className="px-4 py-3 text-right font-medium tabular-nums">
                        <span className="inline-flex items-center gap-2">
                          {money(r.total)}
                          {r.source === "stock" && (
                            <button data-testid={`del-stock-${r.id}`} onClick={() => removeStock(r.id)} className="text-slate-300 hover:text-red-600"><Trash2 className="w-3.5 h-3.5" /></button>
                          )}
                        </span>
                      </td>
                    </tr>
                  ))}
                  {rows.length === 0 && <tr><td colSpan={8 + activeCols.length} className="px-4 py-12 text-center text-slate-400"><Package className="w-8 h-8 mx-auto mb-2 text-slate-300" />{t("noData")}</td></tr>}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}

      {tab === "price" && (
        <div className="space-y-3">
          {groups.map((g, i) => {
            const isOpen = expanded === i;
            return (
              <div key={i} data-testid={`price-group-${i}`} className="bg-white border border-slate-200 rounded-xl shadow-sm overflow-hidden">
                <button onClick={() => setExpanded(isOpen ? null : i)} className="w-full flex items-center justify-between px-5 py-3.5 hover:bg-slate-50">
                  <div className="flex items-center gap-3">
                    <ChevronRight className={`w-4 h-4 text-slate-400 transition-transform ${isOpen ? "rotate-90" : ""}`} />
                    <TrendingUp className="w-4 h-4 text-slate-400" />
                    <div className="text-left">
                      <div className="font-semibold text-slate-900">{g.material_name}</div>
                      <div className="text-xs text-slate-400">{g.count} {t("purchases")}</div>
                    </div>
                  </div>
                  <div className="flex items-center gap-5 text-sm tabular-nums">
                    <div className="text-right hidden sm:block"><div className="text-[10px] uppercase text-slate-400">{t("minPrice")}</div><div className="font-medium text-emerald-600">{money(g.min_price)}</div></div>
                    <div className="text-right hidden sm:block"><div className="text-[10px] uppercase text-slate-400">{t("avgPrice")}</div><div className="font-medium">{money(g.avg_price)}</div></div>
                    <div className="text-right hidden sm:block"><div className="text-[10px] uppercase text-slate-400">{t("maxPrice")}</div><div className="font-medium text-red-600">{money(g.max_price)}</div></div>
                    <div className="text-right"><div className="text-[10px] uppercase text-slate-400">{t("lastPrice")}</div><div className="font-bold">{money(g.last_price)}</div></div>
                  </div>
                </button>
                {isOpen && (
                  <div className="px-5 pb-4 bg-slate-50/60">
                    <table className="w-full text-sm">
                      <thead><tr className="text-[10px] uppercase text-slate-400"><th className="text-left py-1.5">{t("purchasedOn")}</th><th className="text-left">{t("supplier")}</th><th className="text-left">{t("forJob")}</th><th className="text-right">Qty</th><th className="text-right">{t("actualPrice")}</th></tr></thead>
                      <tbody>
                        {g.purchases.map((p) => (
                          <tr key={p.id} className="border-t border-slate-100">
                            <td className="py-1.5 text-slate-500">{fmtDate(p.purchased_date)}</td>
                            <td className="text-slate-600">{p.supplier || "—"}</td>
                            <td className="text-slate-500">{p.unit_name || "—"}</td>
                            <td className="text-right tabular-nums">{p.quantity}</td>
                            <td className="text-right tabular-nums font-medium">{money(p.unit_price)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            );
          })}
          {groups.length === 0 && <div className="bg-white border border-slate-200 rounded-xl p-12 text-center text-slate-400"><Package className="w-8 h-8 mx-auto mb-2 text-slate-300" />{t("noData")}</div>}
        </div>
      )}

      {addOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-2 md:p-4 bg-black/40 backdrop-blur-sm" onClick={() => setAddOpen(false)}>
          <div data-testid="stock-purchase-modal" className="bg-white rounded-xl shadow-xl w-full max-w-lg" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200">
              <h2 className="text-lg font-semibold text-slate-900">{t("addStockPurchase")}</h2>
              <button onClick={() => setAddOpen(false)} className="p-1.5 rounded-md hover:bg-slate-100"><X className="w-5 h-5 text-slate-500" /></button>
            </div>
            <div className="p-6 space-y-4">
              <p className="text-xs text-slate-500 -mt-1">{t("stockPurchaseHint")}</p>
              <div>
                <label className="block text-xs font-bold uppercase tracking-wide text-slate-500 mb-1.5">{t("materialsDb")}</label>
                <input data-testid="stock-name-input" value={addForm.material_name} onChange={(e) => setAddForm({ ...addForm, material_name: e.target.value })} className={`${inputCls} w-full`} />
              </div>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <div>
                  <label className="block text-xs font-bold uppercase tracking-wide text-slate-500 mb-1.5">Qty</label>
                  <input data-testid="stock-qty-input" type="number" step="0.01" value={addForm.quantity} onChange={(e) => setAddForm({ ...addForm, quantity: e.target.value })} className={`${inputCls} w-full`} />
                </div>
                <div>
                  <label className="block text-xs font-bold uppercase tracking-wide text-slate-500 mb-1.5">{t("unitPriceLabel")}</label>
                  <input data-testid="stock-price-input" type="number" step="0.01" value={addForm.unit_price} onChange={(e) => setAddForm({ ...addForm, unit_price: e.target.value })} className={`${inputCls} w-full`} />
                </div>
                <div>
                  <label className="block text-xs font-bold uppercase tracking-wide text-slate-500 mb-1.5">{t("totalPaidLabel")}</label>
                  <input data-testid="stock-total-input" type="number" step="0.01"
                    value={Math.round((parseFloat(addForm.quantity) || 0) * (parseFloat(addForm.unit_price) || 0) * 100) / 100 || ""}
                    onChange={(e) => {
                      const tot = parseFloat(e.target.value) || 0;
                      const q = parseFloat(addForm.quantity) || 1;
                      setAddForm({ ...addForm, unit_price: q ? Math.round((tot / q) * 10000) / 10000 : tot });
                    }} className={`${inputCls} w-full`} />
                </div>
                <div>
                  <label className="block text-xs font-bold uppercase tracking-wide text-slate-500 mb-1.5">{t("purchasedOn")}</label>
                  <input data-testid="stock-date-input" type="date" value={addForm.purchased_date} onChange={(e) => setAddForm({ ...addForm, purchased_date: e.target.value })} className={`${inputCls} w-full`} />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-bold uppercase tracking-wide text-slate-500 mb-1.5">{t("supplier")}</label>
                  <SupplierPicker value={addForm.supplier} onChange={(v) => setAddForm({ ...addForm, supplier: v })} testId="stock-supplier" />
                </div>
                <div>
                  <label className="block text-xs font-bold uppercase tracking-wide text-slate-500 mb-1.5">{t("tradeLabel")}</label>
                  <select data-testid="stock-trade-input" value={addForm.trade} onChange={(e) => setAddForm({ ...addForm, trade: e.target.value })} className={`${inputCls} w-full`}>
                    {SPECIALTIES.map((s) => <option key={s} value={s}>{t(`sp_${s}`)}</option>)}
                  </select>
                </div>
              </div>
              <div>
                <label className="block text-xs font-bold uppercase tracking-wide text-slate-500 mb-1.5">{t("notes")}</label>
                <input data-testid="stock-note-input" value={addForm.note} onChange={(e) => setAddForm({ ...addForm, note: e.target.value })} className={`${inputCls} w-full`} />
              </div>
              <div className="text-right text-sm font-bold text-slate-900">{t("total")}: {money((parseFloat(addForm.quantity) || 0) * (parseFloat(addForm.unit_price) || 0))}</div>
            </div>
            <div className="flex justify-end gap-3 px-6 py-4 border-t border-slate-200">
              <button onClick={() => setAddOpen(false)} className="rounded-lg px-4 py-2 text-sm font-medium border border-slate-300 hover:bg-slate-50">{t("cancel")}</button>
              <button data-testid="stock-save-btn" onClick={saveStock} className="rounded-lg px-4 py-2 text-sm font-medium bg-slate-900 text-white hover:bg-slate-800">{t("save")}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
